import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { consentAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { GraduationCap, Loader2, CheckCircle, XCircle, AlertTriangle, User, Mail, Calendar } from 'lucide-react';

const ParentConsentPage = () => {
  const { consentToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [consentData, setConsentData] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadConsentRequest();
  }, [consentToken]);

  const loadConsentRequest = async () => {
    try {
      const response = await consentAPI.getRequest(consentToken);
      setConsentData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Consent request not found or already processed');
    } finally {
      setLoading(false);
    }
  };

  const handleConsent = async (action) => {
    setProcessing(true);
    try {
      const response = await consentAPI.processConsent(consentToken, action);
      setResult({
        action,
        message: response.data.message,
        studentName: response.data.student_name
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to process consent');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-indigo-700" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 py-12" data-testid="consent-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-4">
            <GraduationCap className="h-10 w-10 text-indigo-700" />
            <span className="font-heading text-2xl font-semibold text-slate-900">
              Gifted Academy Tutors
            </span>
          </Link>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">Parent Consent</h1>
          <p className="text-slate-600 mt-2">Review and approve your child's account</p>
        </div>

        <Card className="shadow-card border-stone-100">
          {error && !result ? (
            <CardContent className="pt-8 text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                Request Not Found
              </h3>
              <p className="text-slate-600 mb-6">{error}</p>
              <Link to="/">
                <Button className="rounded-full">Go to Homepage</Button>
              </Link>
            </CardContent>
          ) : result ? (
            <CardContent className="pt-8 text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                result.action === 'approve' ? 'bg-green-100' : 'bg-red-100'
              }`}>
                {result.action === 'approve' ? (
                  <CheckCircle className="h-8 w-8 text-green-600" />
                ) : (
                  <XCircle className="h-8 w-8 text-red-600" />
                )}
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                {result.action === 'approve' ? 'Consent Approved!' : 'Consent Denied'}
              </h3>
              <p className="text-slate-600 mb-6">
                {result.action === 'approve' 
                  ? `${result.studentName}'s account is now active. They can log in and use Gifted Academy.`
                  : `${result.studentName}'s account access has been denied.`
                }
              </p>
              <Link to="/">
                <Button className="rounded-full">Go to Homepage</Button>
              </Link>
            </CardContent>
          ) : consentData ? (
            <>
              <CardHeader className="text-center">
                <CardTitle className="font-heading text-xl">Consent Request</CardTitle>
                <CardDescription>
                  Please review the following information and provide your consent
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertTriangle className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800 text-sm">
                    Your child has requested to create an account on Gifted Academy Tutors. 
                    As they are under 18, we require your consent.
                  </AlertDescription>
                </Alert>

                <div className="bg-stone-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Student Name</p>
                      <p className="font-medium text-slate-900">{consentData.student_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Student Email</p>
                      <p className="font-medium text-slate-900">{consentData.student_email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Student Age</p>
                      <p className="font-medium text-slate-900">{consentData.student_age} years old</p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 font-medium mb-2">By approving, you consent to:</p>
                  <ul className="text-sm text-amber-700 list-disc list-inside space-y-1">
                    <li>Your child creating an account on Gifted Academy</li>
                    <li>Your child booking tutoring sessions online</li>
                    <li>Your child participating in video tutoring sessions</li>
                    <li>Receiving notifications about your child's activity</li>
                  </ul>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => handleConsent('deny')}
                    disabled={processing}
                    className="flex-1 rounded-full border-red-200 text-red-700 hover:bg-red-50"
                    data-testid="deny-consent-btn"
                  >
                    {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
                    Deny
                  </Button>
                  <Button
                    onClick={() => handleConsent('approve')}
                    disabled={processing}
                    className="flex-1 rounded-full bg-green-600 hover:bg-green-700"
                    data-testid="approve-consent-btn"
                  >
                    {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Approve
                  </Button>
                </div>

                <p className="text-xs text-center text-slate-500">
                  If you did not expect this request, please ignore it or contact support.
                </p>
              </CardContent>
            </>
          ) : null}
        </Card>
      </motion.div>
    </div>
  );
};

export default ParentConsentPage;
